import React, { useMemo, useRef, useState } from 'react';
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { adminEmailAPI } from '../services/api';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 50;
const MAX_MESSAGE_LENGTH = 10000;

const parseRecipients = (value) => (
  value
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)
);

const getEditorText = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  return container.textContent || '';
};

const EmailCompose = () => {
  const editorRef = useRef(null);
  const [formData, setFormData] = useState({
    recipients: '',
    subject: '',
    body: '',
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const recipients = useMemo(
    () => parseRecipients(formData.recipients),
    [formData.recipients]
  );

  const invalidRecipients = useMemo(
    () => recipients.filter((email) => !EMAIL_PATTERN.test(email)),
    [recipients]
  );

  const updateField = (field) => (event) => {
    setFormData((current) => ({ ...current, [field]: event.target.value }));
    setResult(null);
    setError('');
  };

  const formatMessage = (command) => {
    if (!editorRef.current) {
      return;
    }

    editorRef.current.focus();
    document.execCommand(command, false);
    const body = editorRef.current.innerHTML;
    setFormData((current) => ({
      ...current,
      body,
    }));
    setResult(null);
    setError('');
  };

  const handleEditorInput = (event) => {
    const body = event.currentTarget.innerHTML;
    setFormData((current) => ({
      ...current,
      body,
    }));
    setResult(null);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(null);
    setError('');

    if (recipients.length === 0) {
      setError('Enter at least one recipient email address.');
      return;
    }

    if (recipients.length > MAX_RECIPIENTS) {
      setError(`You can send to a maximum of ${MAX_RECIPIENTS} recipients at once.`);
      return;
    }

    if (invalidRecipients.length > 0) {
      setError(`These addresses are invalid: ${invalidRecipients.join(', ')}`);
      return;
    }

    if (!formData.subject.trim() || !getEditorText(formData.body).trim()) {
      setError('Subject and message are required.');
      return;
    }

    const confirmed = window.confirm(
      `Send this email immediately to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}?`
    );
    if (!confirmed) {
      return;
    }

    setSending(true);
    try {
      const response = await adminEmailAPI.sendEmail({
        recipients,
        subject: formData.subject.trim(),
        body: formData.body,
      });

      if (response.status === 'success') {
        setResult(response.data || { sent: recipients.length, failed: [] });
        setFormData({ recipients: '', subject: '', body: '' });
        if (editorRef.current) {
          editorRef.current.innerHTML = '';
        }
      } else {
        setError(response.message || 'The email could not be sent.');
      }
    } catch (requestError) {
      const validationErrors = requestError.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors).flat()[0]
        : null;
      setError(
        firstValidationError
        || requestError.response?.data?.message
        || 'The email could not be sent. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Send Email</h2>
        <p className="mt-1 text-sm text-gray-500">
          Send a branded message immediately to one or more email addresses.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800" role="status">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-medium">
                {result.sent || 0} email{result.sent === 1 ? '' : 's'} sent successfully.
              </p>
              {result.failed?.length > 0 && (
                <p className="mt-1 text-red-700">
                  Failed recipients: {result.failed.map((item) => item.email || item).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={handleSubmit} className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200 lg:col-span-2">
          <div className="space-y-5">
            <div>
              <label htmlFor="email-recipients" className="block text-sm font-medium text-gray-700">
                Recipient email addresses
              </label>
              <input
                id="email-recipients"
                type="text"
                value={formData.recipients}
                onChange={updateField('recipients')}
                placeholder="person@example.com, another@example.com"
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate addresses with commas. Up to {MAX_RECIPIENTS} recipients.
              </p>
            </div>

            <div>
              <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                id="email-subject"
                type="text"
                value={formData.subject}
                onChange={updateField('subject')}
                placeholder="Important update from Aurora Wealth Reserves"
                maxLength={255}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="email-body" className="block text-sm font-medium text-gray-700">
                Message
              </label>
              <div className="mt-1 flex flex-wrap gap-1 rounded-t-lg border border-gray-300 bg-gray-50 p-2">
                {[
                  { command: 'bold', label: 'B', title: 'Bold' },
                  { command: 'italic', label: 'I', title: 'Italic' },
                  { command: 'underline', label: 'U', title: 'Underline' },
                  { command: 'insertUnorderedList', label: '• List', title: 'Bullet list' },
                  { command: 'insertOrderedList', label: '1. List', title: 'Numbered list' },
                  { command: 'removeFormat', label: 'Clear', title: 'Clear formatting' },
                ].map((tool) => (
                  <button
                    key={tool.command}
                    type="button"
                    title={tool.title}
                    aria-label={tool.title}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => formatMessage(tool.command)}
                    className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <span className={tool.command === 'bold' ? 'font-bold' : ''}>
                      {tool.command === 'italic' ? <em>{tool.label}</em> : tool.label}
                    </span>
                  </button>
                ))}
              </div>
              <div
                id="email-body"
                ref={editorRef}
                contentEditable
                role="textbox"
                aria-multiline="true"
                aria-label="Message"
                data-placeholder="Write your message here..."
                onInput={handleEditorInput}
                className="min-h-[240px] w-full rounded-b-lg border-x border-b border-gray-300 bg-white p-3 text-sm text-gray-900 shadow-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)]"
              />
              <p className="mt-1 text-right text-xs text-gray-500">
                {getEditorText(formData.body).length.toLocaleString()} / {MAX_MESSAGE_LENGTH.toLocaleString()}
              </p>
            </div>

            <button
              type="submit"
              disabled={sending}
              className="inline-flex w-full items-center justify-center rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" />
                  Sending email...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="mr-2 h-5 w-5" />
                  Send Email Immediately
                </>
              )}
            </button>
          </div>
        </form>

        <aside className="rounded-xl bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 p-6 text-white shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
            <EnvelopeIcon className="h-6 w-6" />
          </div>
          <h3 className="mt-5 text-lg font-semibold">Branded delivery</h3>
          <p className="mt-2 text-sm leading-6 text-primary-100">
            Every message is wrapped in the Aurora Wealth Reserves email template with the company logo and a plain-text fallback.
          </p>
          <div className="mt-6 border-t border-white/20 pt-5 text-sm text-primary-100">
            <p>Messages are sent immediately.</p>
            <p className="mt-2">Recipients and message content are not saved in the database.</p>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default EmailCompose;
